import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../features/Cart/useCart";
import Button from "../../components/Button";
import { toPersianNumbers } from "../../utils/toPersianNumbers";
import QuantitySelector from "../../components/Basket/QuantitySelector";

const CartPage = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const {
    cart,
    warnings,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    updateQuantity,
    removeItem,
    clearCart,
    validateCart,
  } = useCart({
    enabled: isLoggedIn,
  });

  const [updatingItems, setUpdatingItems] = useState({});

  const updating =
    updateQuantity.isPending ||
    removeItem.isPending ||
    clearCart.isPending ||
    validateCart.isPending;

  const items = cart?.items ?? [];
  const itemsCount = cart?.items_count ?? 0;

  const showInitialLoading =
    isLoggedIn && cart === undefined && (isLoading || isFetching);

  const isCartEmpty =
    cart !== undefined &&
    Array.isArray(cart?.items) &&
    cart.items.length === 0;

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login?redirect=cart");
    }
  }, [isLoggedIn, navigate]);

  const handleRetry = () => {
    refetch?.();
  };

  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(itemId);
      return;
    }

    setUpdatingItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    try {
      await updateQuantity.mutateAsync({
        itemId,
        quantity: newQuantity,
      });

      validateCart.mutate();
    } catch (error) {
      console.error("Error updating quantity:", error);

      if (error.response?.data?.error) {
        alert(error.response.data.error);
      }
    } finally {
      setUpdatingItems((prev) => ({
        ...prev,
        [itemId]: false,
      }));
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm("آیا از حذف این کالا اطمینان دارید؟")) {
      return;
    }

    setUpdatingItems((prev) => ({
      ...prev,
      [itemId]: true,
    }));

    try {
      await removeItem.mutateAsync(itemId);
      validateCart.mutate();
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      setUpdatingItems((prev) => ({
        ...prev,
        [itemId]: false,
      }));
    }
  };

  const handleClearCart = async () => {
    if (!window.confirm("آیا از خالی کردن کامل سبد خرید اطمینان دارید؟")) {
      return;
    }

    try {
      await clearCart.mutateAsync();
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const handleCheckout = () => {
    navigate("/checkout/checkout/");
  };

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString("fa-IR");
  };

  const getUnitPrice = (item) => {
    const price = Number(item.price_at_time || 0);
    const discount = Number(item.discount_at_time || 0);

    return price - (discount * price) / 100;
  };

  if (showInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-[#1E3A5F]" />
          <p className="mt-4 text-sm text-gray-600">
            در حال دریافت اطلاعات سبد خرید...
          </p>
        </div>
      </div>
    );
  }

  if (isError && cart === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center sm:p-8">
          <svg
            className="mx-auto mb-4 h-16 w-16 text-[#E74C3C]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>

          <h3 className="mb-2 text-base font-semibold text-gray-900 sm:text-lg">
            خطا
          </h3>

          <p className="mb-4 text-sm text-gray-600">
            {error?.response?.data?.error || "خطا در دریافت اطلاعات سبد خرید"}
          </p>

          <Button variant="primary" onClick={handleRetry}>
            تلاش مجدد
          </Button>
        </div>
      </div>
    );
  }

  if (isCartEmpty) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-10 sm:py-16">
        <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white px-4 py-10 text-center shadow-sm sm:px-6 sm:py-12">
          <svg
            className="mx-auto mb-6 h-20 w-20 text-gray-400 sm:h-24 sm:w-24"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>

          <h2 className="mb-4 text-xl font-semibold text-[#1E3A5F] sm:text-2xl">
            سبد خرید شما خالی است
          </h2>

          <p className="mb-8 text-sm leading-6 text-gray-600 sm:text-base">
            می‌توانید با مراجعه به فروشگاه، محصولات مورد نظر خود را انتخاب کنید
          </p>

          <Link to="/">
            <Button variant="primary" size="large">
              بازگشت به فروشگاه
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!cart) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8">
        <div className="mb-4 flex items-center justify-between gap-3 sm:mb-6">
          <div>
            <h1 className="text-xl font-semibold text-[#1E3A5F] sm:text-2xl">
              سبد خرید
            </h1>
            <p className="mt-1 text-xs text-gray-500 sm:text-sm">
              بررسی و نهایی‌سازی اقلام انتخاب‌شده
            </p>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="mb-6 rounded-2xl border border-[#F59E0B]/20 bg-[#FFF7E6] p-4 shadow-sm sm:p-5">
            <div className="flex items-start gap-3">
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#D19D00]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>

              <div className="min-w-0 flex-1">
                <h4 className="mb-2 text-sm font-semibold text-gray-800">
                  توجه: موارد زیر نیاز به بررسی دارند
                </h4>

                <ul className="space-y-1 text-sm text-gray-700">
                  {warnings.map((warning, index) => (
                    <li key={index} className="leading-6">
                      • {warning.message || warning}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div
                  className="hidden md:grid md:grid-cols-12 md:gap-4 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs font-medium text-gray-600 sm:px-5">
                <div className="md:col-span-6">محصول</div>
                <div className="md:col-span-2 text-center">قیمت واحد</div>
                <div className="md:col-span-2 text-center">تعداد</div>
                <div className="md:col-span-2 text-center">جمع</div>
              </div>

              <div className="divide-y divide-gray-100">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="px-4 py-4 transition-colors hover:bg-gray-50 sm:px-5 md:grid md:grid-cols-12 md:items-center md:gap-4"
                    >
                      {/* تصویر + نام کالا */}
                      <div className="flex min-w-0 gap-3 md:col-span-6">
                        <div
                            className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                          {item.product?.featured_image_url ? (
                              <Link
                                  to={`/shopping/product-detail/${item.product.slug}`}
                                  className="block h-full w-full"
                              >
                                <img
                                    src={item.product.featured_image_url}
                                    alt={item.product.name}
                                    className="h-full w-full object-cover"
                                />
                              </Link>
                          ) : (
                              <div className="flex h-full w-full items-center justify-center text-gray-400">
                                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <Link
                              to={`/shopping/product-detail/${item.product?.slug}`}
                              className="block line-clamp-2 text-sm font-semibold leading-6 text-gray-900 transition-colors hover:text-[#1E3A5F]"
                          >
                            {item.product?.name}
                          </Link>
                          <p className="mt-1 text-xs text-gray-500 sm:text-sm">{item.supplier_name}</p>

                          {/* جمع کل فقط در موبایل، زیر نام کالا */}
                          <div className="mt-1.5 flex items-center gap-1.5 md:hidden">
                            <span className="text-xs text-gray-500">جمع:</span>
                            <span className="text-sm font-bold text-[#1E3A5F]">
              {formatPrice(item.final_price)} تومان
            </span>
                          </div>
                        </div>
                      </div>

                      {/* باکس قیمت + تعداد در موبایل — کنار هم، شکیل و جمع‌وجور */}
                      <div
                          className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2.5 md:hidden">
                        <div className="min-w-0">
                          <div className="text-[11px] text-gray-500">قیمت واحد</div>
                          <div className="text-sm font-semibold text-[#1E3A5F]">
                            {formatPrice(getUnitPrice(item))} تومان
                          </div>
                          {item.discount_at_time > 0 && (
                              <div className="text-[11px] text-gray-400 line-through">
                                {formatPrice(item.price_at_time)} تومان
                              </div>
                          )}
                        </div>

                        <QuantitySelector
                            value={item.quantity}
                            onChange={(newQty) => handleUpdateQuantity(item.id, newQty)}
                            onRemove={() => handleRemoveItem(item.id)}
                            showRemoveButton={true}
                            min={item.min_order}
                            max={item.max_order}
                            multiple={item.order_multiple}
                            loading={
                                Boolean(updatingItems[item.id]) ||
                                updateQuantity.isPending ||
                                removeItem.isPending
                            }
                            inventoryCount={item.product?.inventory_count}
                            showInventory={false}
                            className="flex-shrink-0"
                            itemId={item.id}
                        />
                      </div>

                      {/* ستون‌های دسکتاپ — بدون تغییر نسبت به قبل */}
                      <div className="hidden text-center md:col-span-2 md:block">
                        <div className="text-sm font-semibold text-[#1E3A5F]">
                          {formatPrice(getUnitPrice(item))} تومان
                        </div>
                        {item.discount_at_time > 0 && (
                            <div className="mt-1 text-xs text-gray-400 line-through">
                              {formatPrice(item.price_at_time)} تومان
                            </div>
                        )}
                      </div>

                      <div className="hidden justify-center md:col-span-2 md:flex">
                        <QuantitySelector
                            value={item.quantity}
                            onChange={(newQty) => handleUpdateQuantity(item.id, newQty)}
                            onRemove={() => handleRemoveItem(item.id)}
                            showRemoveButton={true}
                            min={item.min_order}
                            max={item.max_order}
                            multiple={item.order_multiple}
                            loading={
                                Boolean(updatingItems[item.id]) ||
                                updateQuantity.isPending ||
                                removeItem.isPending
                            }
                            inventoryCount={item.product?.inventory_count}
                            showInventory={false}
                            className="flex-shrink-0"
                            itemId={item.id}
                        />
                      </div>

                      <div className="hidden text-center md:col-span-2 md:block">
                        <div className="text-sm font-bold text-[#1E3A5F]">
                          {formatPrice(item.final_price)} تومان
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            </div>

            <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                  onClick={handleClearCart}
                  disabled={updating}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-[#E74C3C]/20 px-4 py-2 text-sm font-medium text-[#E74C3C] transition-colors duration-200 hover:bg-[#FDF2F2] hover:text-[#C0392B] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                خالی کردن سبد خرید
              </button>

              <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-[#1E3A5F] shadow-sm transition-colors duration-200 hover:bg-gray-50 hover:text-[#2B5388]"
              >
                <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                ادامه خرید
              </Link>
            </div>
          </div>

          <div className="lg:sticky lg:top-4 h-fit">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
              <h3 className="mb-4 text-base font-semibold text-[#1E3A5F]">
                خلاصه سبد خرید
              </h3>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600">تعداد کالاها:</span>
                  <span className="font-medium text-gray-900">
                    {toPersianNumbers(itemsCount)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600">جمع کل:</span>
                  <span className="font-medium text-gray-900">
                    {formatPrice(cart.subtotal)} تومان
                  </span>
                </div>

                {cart.total_discount > 0 && (
                    <div className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-gray-600">تخفیف:</span>
                      <span className="font-medium text-[#00AFA9]">
                      {formatPrice(cart.total_discount)} تومان
                    </span>
                    </div>
                )}

                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-sm font-semibold text-gray-900">
                      مبلغ قابل پرداخت:
                    </span>

                    <span className="text-lg font-bold text-[#1E3A5F] sm:text-xl">
                      {formatPrice(cart.final_total)} تومان
                    </span>
                  </div>
                </div>
              </div>

              <Button
                  variant="primary"
                  size="large"
                  fullWidth
                  onClick={handleCheckout}
                  disabled={updating || itemsCount === 0}
              >
                جزئیات ارسال
              </Button>

              <p className="mt-4 text-center text-xs leading-5 text-gray-500">
                جزئیات ارسال در مراحل بعدی تعیین خواهد شد.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
